import { useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';

import type PopperJS from '@popperjs/core';
import clsx from 'clsx';

// Types
import { type SizesType, SM } from '@/types/utils/sizesType';
// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';

// Components
import Icon from '@/components/base/Icon';
import CustomPopover from '@/components/composite/Popover/CustomPopover';
import type { ProfileType } from '@/components/composite/SpGroups/SpOrSpGroupAvatar';
import SpOrSpGroupAvatar from '@/components/composite/SpGroups/SpOrSpGroupAvatar';

type SocialProfilesAndGroupsPopoverProps = {
    profiles: ProfileType[];
    btnClassNames?: string;
    placement?: PopperJS.Placement;
};

// Only import this component if you just want the popover
export const SocialProfilesAndGroupsPopover = ({
    profiles,
    btnClassNames,
    placement = 'bottom-start'
}: SocialProfilesAndGroupsPopoverProps) => {
    const { isOpen: isPopoverOpen, toggle: togglePopover } = useToggle();
    const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>(null);
    const [popperElement, setPopperElement] = useState<Parameters<typeof usePopper>[1]>(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: placement
    });

    return (
        <>
            <button
                className={clsx(
                    'flex h-[42px] w-[42px] items-center justify-center rounded-full text-xs text-icoBlue',
                    btnClassNames
                )}
                ref={setReferenceElement}
                onClick={(e) => {
                    e.preventDefault();
                    togglePopover();
                }}
                data-testid="popover-button">
                <Icon icon="plus" />
                {profiles.length}
            </button>
            {isPopoverOpen
                ? ReactDOM.createPortal(
                      <CustomPopover
                          open={isPopoverOpen}
                          hasBtnClose={false}
                          hasArrow={false}
                          wrapperClassName="z-100"
                          popperElement={popperElement}
                          setPopperElement={setPopperElement}
                          styles={{
                              popper: styles.popper
                          }}
                          width="w-48"
                          toggle={togglePopover}
                          attributes={attributes}>
                          <div className="absolute z-20 mt-1 rounded-m bg-white p-1 shadow-icoM dark:bg-icoDarkMode-anthracite">
                              <div
                                  className="scrollbar max-h-80 w-56 space-y-3 overflow-y-auto overflow-x-hidden p-4"
                                  data-testid="popover-content">
                                  {profiles?.map((profile) => (
                                      <div
                                          key={
                                              profile?.id + ('slug' in profile ? profile?.slug : '')
                                          }
                                          className="flex items-center gap-4">
                                          <SpOrSpGroupAvatar
                                              size={SM}
                                              item={profile}
                                              disabled={
                                                  !profile?.active && profile?.active !== null
                                              }
                                          />
                                          <div className="flex flex-1 flex-col overflow-hidden">
                                              <p className="truncate text-sm">
                                                  {'slug' in profile
                                                      ? profile?.slug
                                                      : profile?.name}
                                              </p>
                                              {'external_api_data' in profile ? (
                                                  <p className="truncate text-xs text-icoGray-500">
                                                      {profile?.external_api_data?.name}
                                                  </p>
                                              ) : null}
                                          </div>
                                      </div>
                                  ))}
                              </div>
                          </div>
                      </CustomPopover>,
                      document.body
                  )
                : null}
        </>
    );
};

type SocialProfilesAndGroupsListPopoverProps = SocialProfilesAndGroupsPopoverProps & {
    maxDisplayed: number;
    avatarSize?: SizesType;
    className?: string;
    avatarsClassName?: string;
    placement?: PopperJS.Placement;
};

const SocialProfilesAndGroupsListPopover = ({
    profiles,
    maxDisplayed,
    avatarSize = 'sm',
    className,
    avatarsClassName,
    btnClassNames,
    placement = 'bottom-start'
}: SocialProfilesAndGroupsListPopoverProps) => {
    if (!profiles?.length) {
        return null;
    }

    let displayed = profiles?.slice(0, maxDisplayed);
    const inPopover = profiles?.slice(maxDisplayed);

    if (inPopover.length === 1) {
        displayed = [...displayed, ...inPopover];
    }

    return (
        <div className={clsx('flex items-center', className)} data-testid="social-profile-list">
            {displayed?.map((profile) => (
                <SpOrSpGroupAvatar
                    key={profile?.id + ('slug' in profile ? profile?.slug : '')}
                    item={profile}
                    className={clsx(avatarsClassName)}
                    size={avatarSize}
                    disabled={!profile?.active && profile?.active !== null}
                />
            ))}
            {inPopover?.length > 1 ? (
                <SocialProfilesAndGroupsPopover
                    btnClassNames={btnClassNames}
                    profiles={inPopover}
                    placement={placement}
                />
            ) : null}
        </div>
    );
};

export default SocialProfilesAndGroupsListPopover;
